import { haversineDistanceM, interpolateGreatCircle } from '@freedom/geospatial';
import type { Coordinates } from '@freedom/shared';

const ROUTE_SEGMENT_LENGTH_M = 10_000;
const MAX_ROUTE_SEGMENTS = 128;

/**
 * Converts a distance on the terrain profile into the matching point on the
 * PtP great-circle route. A supplied profile length keeps the map cursor in
 * sync when the sampled profile and geodesic length differ by a few metres.
 */
export function profileMapCoordinateAtDistance(
  start: Coordinates,
  end: Coordinates,
  distanceM: number,
  profileTotalDistanceM = haversineDistanceM(start, end),
): Coordinates {
  const routeDistanceM = Number.isFinite(profileTotalDistanceM) && profileTotalDistanceM > 0
    ? profileTotalDistanceM
    : haversineDistanceM(start, end);

  if (routeDistanceM <= 0 || Number.isNaN(distanceM) || distanceM === Number.NEGATIVE_INFINITY) return { ...start };
  if (distanceM <= 0) return { ...start };
  if (distanceM >= routeDistanceM || distanceM === Number.POSITIVE_INFINITY) return { ...end };
  return interpolateGreatCircle(start, end, distanceM / routeDistanceM);
}

/**
 * Densifies the rendered radio link along the same great-circle route used by
 * DEM sampling and the profile hover marker. This avoids a visible mismatch
 * between a straight Web-Mercator chord and the engineering path.
 */
export function profileMapRouteCoordinates(start: Coordinates, end: Coordinates): Coordinates[] {
  const distanceM = haversineDistanceM(start, end);
  const segmentCount = Math.max(1, Math.min(MAX_ROUTE_SEGMENTS, Math.ceil(distanceM / ROUTE_SEGMENT_LENGTH_M)));
  return Array.from({ length: segmentCount + 1 }, (_, index) => {
    if (index === 0) return { ...start };
    if (index === segmentCount) return { ...end };
    return interpolateGreatCircle(start, end, index / segmentCount);
  });
}
